import type { AttributeTypeKey } from 'BlockList/types';

/**
 * Per-type validation for the Attribute Value field(s).
 * Returns an error string when invalid, or undefined when valid.
 *
 * Validation rules:
 *   email         — non-empty, contains "@" and a "." after "@"
 *   email_domain  — non-empty, no "@", contains "."
 *   phone_number  — digits only (after stripping spaces/hyphens/parens), 10 digits
 *   vin           — exactly 17 alphanumeric characters (case-insensitive), no I/O/Q
 *   drivers_license — state: exactly 2 alpha chars; number: non-empty
 */

/** Single-value types */
export function validateAttributeValue(
  type: Exclude<AttributeTypeKey, 'drivers_license' | 'name_dob'>,
  value: string
): string | undefined;

/** drivers_license: two-field tuple */
export function validateAttributeValue(
  type: 'drivers_license',
  value: { state: string; number: string }
): string | undefined;

/** Overload implementation */
export function validateAttributeValue(
  type: AttributeTypeKey,
  value: string | { state: string; number: string }
): string | undefined {
  switch (type) {
    case 'email': {
      const v = (value as string).trim();
      if (!v) return 'Email is required.';
      const atIdx = v.indexOf('@');
      if (atIdx < 1) return 'Enter a valid email address.';
      const domain = v.slice(atIdx + 1);
      if (!domain.includes('.') || domain.startsWith('.')) {
        return 'Enter a valid email address.';
      }
      return undefined;
    }
    case 'email_domain': {
      const v = (value as string).trim();
      if (!v) return 'Email domain is required.';
      if (v.includes('@')) return 'Enter a domain only (no "@" symbol).';
      if (!v.includes('.')) return 'Enter a valid domain (e.g. example.com).';
      return undefined;
    }
    case 'phone_number': {
      const v = (value as string).replace(/[\s\-().]/g, '');
      if (!v) return 'Phone number is required.';
      if (!/^\d{10}$/.test(v)) return 'Enter a 10-digit phone number.';
      return undefined;
    }
    case 'vin': {
      const v = (value as string).trim().toUpperCase();
      if (!v) return 'VIN is required.';
      if (v.length !== 17) return 'VIN must be exactly 17 characters.';
      if (/[IOQ]/.test(v)) return 'VIN cannot contain I, O, or Q.';
      if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(v)) {
        return 'VIN must contain only alphanumeric characters (no I, O, Q).';
      }
      return undefined;
    }
    case 'drivers_license': {
      const { state, number } = value as { state: string; number: string };
      if (
        !state ||
        state.trim().length !== 2 ||
        !/^[A-Za-z]{2}$/.test(state.trim())
      ) {
        return 'State must be a 2-letter abbreviation.';
      }
      const trimmedNumber = (number ?? '').trim().toUpperCase();
      if (!trimmedNumber) {
        return 'License number is required.';
      }
      // Loose generic format — matches the union of US state DL formats
      // without false-negatives on any single state. Backend Reznor R2
      // performs the canonical normalization.
      if (!/^[A-Z0-9-]{4,20}$/.test(trimmedNumber)) {
        return 'Enter a valid driver license (letters, digits, hyphens; 4–20 chars).';
      }
      return undefined;
    }
    default:
      return undefined;
  }
}
